import { addDays, addMonths, format, isToday, subDays, subMonths } from 'date-fns'

import type { DashboardWidget, DashboardWidgetBody } from '@/stores/dashboard/types'

export const DASHBOARD_WIDGET_COLUMNS = {
    SMALL: { cols: 12, sm: 6, md: 4, lg: 3, xl: 3 },
    LARGE: { cols: 12, sm: 6, md: 6, lg: 8, xl: 8 }
} as const

export type DashboardWidgetSize = 'small' | 'large'

export function formatDateForApi(value: Date | string | number): string {
    return format(new Date(value), 'yyyy-MM-dd')
}

export function formatDateForDisplay(value: Date | string | number | null | undefined, withTime = false): string {
    if (!value) return '-'

    return format(new Date(value), withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy')
}

export function formatNumber(value: unknown): string {
    return new Intl.NumberFormat('pl-PL').format(Number(value) || 0)
}

export function buildQueryString(
    filters: Record<string, string | number | null | undefined>,
    mapKey?: (param: string) => string
): string {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([param, value]) => {
        if (value === null || value === undefined || value === '') return
        params.append(mapKey ? mapKey(param) : param, String(value))
    })

    const query = params.toString()
    return query ? `?${query}` : ''
}

export function getCommonHeaders(title: string) {
    return [{ title, key: 'value', sortable: false }] as const
}

export function getDashboardWidgetDisplayTitle(widget: DashboardWidget | null | undefined): string {
    const body = widget?.body
    const bodyTitle = body && typeof body.title === 'string' ? body.title : undefined
    return widget?.title || bodyTitle || widget?.verboseName || widget?.codeName || 'Dashboard'
}

/** Table rows from widget `body` (API shape varies: data / content / results). */
export function getDashboardWidgetBodyRows(body: DashboardWidgetBody | null | undefined): unknown[] {
    if (!body || typeof body !== 'object') return []

    const candidates = [body.data, body.content, body.results]
    for (const raw of candidates) {
        if (Array.isArray(raw)) return raw
        if (raw && typeof raw === 'object' && Array.isArray((raw as { results?: unknown }).results)) {
            return (raw as { results: unknown[] }).results
        }
    }

    return []
}

export function getWidgetBodyContent(widget: DashboardWidget | null | undefined): unknown[] {
    const body = widget?.body
    if (!body) return []
    const content = body.content
    if (Array.isArray(content)) return content
    const data = body.data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const results = (data as { results?: unknown }).results
        if (Array.isArray(results)) return results
    }
    if (Array.isArray(data)) return data as unknown[]
    return []
}

export function getWidgetBodyCount(widget: DashboardWidget | null | undefined): number {
    const n = widget?.body?.count
    return typeof n === 'number' ? n : Number(n) || 0
}

export function getCurrentMonthLabel(date: Date): string {
    return format(date, 'LLLL yyyy')
}

export interface MonthNavigationState {
    currentDate: Date
    minStep: number
    maxStep: number
    targetStep: number
}

export function createMonthNavigationState(): MonthNavigationState {
    return {
        currentDate: new Date(),
        minStep: 1,
        maxStep: 4,
        targetStep: 4
    }
}

export function moveMonthBack(state: MonthNavigationState) {
    state.targetStep -= 1
    state.currentDate = subMonths(state.currentDate, 1)
}

export function moveMonthForward(state: MonthNavigationState) {
    state.targetStep += 1
    state.currentDate = addMonths(state.currentDate, 1)
}

export interface DayNavigationState {
    currentDate: Date
    dayOffset: number
}

export function moveDayBack(state: DayNavigationState) {
    state.dayOffset += 1
    state.currentDate = subDays(state.currentDate, 1)
}

export function moveDayForward(state: DayNavigationState) {
    state.dayOffset -= 1
    state.currentDate = addDays(state.currentDate, 1)
}

export function isForwardDayDisabled(currentDate: Date): boolean {
    return isToday(currentDate)
}

export function getOrdersLocationSortRank(value: unknown): number {
    const ranks: Record<string, number> = {
        Online: 0,
        Drukomat: 1,
        WMD: 2,
        Czeskie: 3,
        Offline: 4,
        null: 5,
        undefined: 5
    }
    const key = String(value)
    return ranks[key] ?? 6
}

export function normalizeWidgetKey(value: unknown): string {
    if (value == null || value === '') return ''

    return String(value)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toLowerCase()
}

const LEGACY_DASHBOARD_REDIRECT_ROUTE_NAMES: Record<string, string> = {
    complaints: 'quality',
    complaintsComplaints: 'qualityComplaintsList',
    complaintsComplaintsCreate: 'qualityComplaintsExternalCreate',
    complaintsQualitativeNotesList: 'qualityQualitativeNotes',
    stopCards: 'qualityStopCards',
    complaintsStorageComplaintsList: 'qualityStorageComplaints',
    codeCompliancesList: 'qualityCodeCompliance'
}

export function resolveDashboardRedirectRouteName(name: unknown): string | undefined {
    if (name == null || name === '') return undefined

    const key = String(name)
    return LEGACY_DASHBOARD_REDIRECT_ROUTE_NAMES[key] ?? key
}
